/* eslint-disable react/no-array-index-key */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Grid,
  Link as MuiLink,
  Breadcrumbs,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  IconButton,
  Skeleton,
  Alert,
  Button,
  ListItemAvatar,
  Avatar,
  TextField,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import NotesIcon from '@mui/icons-material/Notes';
import SendIcon from '@mui/icons-material/Send';
import { TableContainerProps as MuiTableContainerProps } from '@mui/material/TableContainer';
import { styled, useTheme } from '@mui/material/styles';
import { useParams } from 'react-router-dom';

// import { useTheme } from '@mui/material/styles';
import { DownloadOutlined } from '@mui/icons-material';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { formatQueryError } from 'renderer/shared/formatters/formatters';
import { useAppSelector } from 'renderer/shared/state/hooks';
import { selectIsAdmin } from 'renderer/features/auth/state/userSelectors';
import {
  useGetTransactionDetailsQuery,
  useUpdateTransactionNoteMutation,
} from '../services/transactions.endpoints';
import ExportPDFView from '../../users-page/components/export-pdf';
import { TransactionDetails } from '../schemas/transaction.schemas';
import {
  getProductDescription,
  transformTransactionToInvoice,
} from '../schemas/transaction.transformer';
import RefundDialog from './RefundDialog';

const PDFIconButton = ({
  transaction,
}: {
  transaction: TransactionDetails;
}) => {
  const theme = useTheme();
  const transactionInfo = transformTransactionToInvoice(transaction);
  return (
    <PDFDownloadLink
      document={<ExportPDFView transaction={transactionInfo} />}
      fileName={`${transaction._id}.pdf`}
    >
      <Tooltip title="Download" placement="top">
        <IconButton>
          <DownloadOutlined style={{ color: theme.palette.grey[900] }} />
        </IconButton>
      </Tooltip>
    </PDFDownloadLink>
  );
};

const MainContentWrapper = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[3],
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
  margin: theme.spacing(2, 0),
}));

interface TableContainerProps extends MuiTableContainerProps {
  component?: React.ElementType;
}

const StyledTableContainer = styled(TableContainer)<TableContainerProps>(
  ({ theme }) => ({
    boxShadow: 'none',
    '& .MuiTableCell-head': {
      backgroundColor: theme.palette.action.hover,
    },
  }),
);

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  boxShadow: 'none',
  borderRadius: 0,
}));

const formatCurrency = (amount: number): string => `$${amount.toFixed(2)}`;

const PaymentDetailsPage: React.FC = () => {
  const { id } = useParams();
  const isAdmin = useAppSelector(selectIsAdmin);
  const { data: transaction, error } = useGetTransactionDetailsQuery(id || '');
  const [updateNote, { isLoading: updateLoading }] =
    useUpdateTransactionNoteMutation();
  const [refundModalOpen, setRefundModalOpen] = useState(false);
  const [editNote, setEditNote] = useState(false);
  const [note, setNote] = useState('');

  const handleEditNoteClick = useCallback(() => {
    if (editNote && transaction) {
      updateNote({ transactionId: transaction._id, note });
      setEditNote(false);
      return;
    }
    setEditNote(true);
  }, [editNote, note, transaction, updateNote]);

  useEffect(() => {
    if (transaction && transaction.note) {
      setNote(transaction.note);
    }
  }, [transaction]);

  if (error) return <Alert severity="error">{formatQueryError(error)}</Alert>;

  if (!transaction) return <Skeleton />;

  return (
    <Container>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
        <MuiLink underline="hover" color="inherit" href="/">
          Home
        </MuiLink>
        <MuiLink underline="hover" color="inherit" href="/#/transactions">
          Transactions
        </MuiLink>
        <Typography color="text.primary">Transaction Details</Typography>
      </Breadcrumbs>
      <Typography variant="h5" gutterBottom>
        Transaction Detail
      </Typography>

      <PDFIconButton transaction={transaction} />

      {isAdmin && (
        <Button
          variant="outlined"
          color="error"
          onClick={() => setRefundModalOpen(true)}
        >
          Issue Refund
        </Button>
      )}
      {isAdmin && (
        <RefundDialog
          open={refundModalOpen}
          setOpen={setRefundModalOpen}
          transaction={transaction}
        />
      )}

      <MainContentWrapper>
        <StyledPaper>
          <Typography variant="h6">User Information</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography>
                Name: {transaction.firstName} {transaction.lastName}
              </Typography>
              <Typography>Email: {transaction.email}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography>Status: {transaction.status}</Typography>
              {transaction.refundedAmount > 0 && (
                <Typography color="error">
                  Refunded: {formatCurrency(transaction.refundedAmount)}
                </Typography>
              )}
            </Grid>
          </Grid>
        </StyledPaper>
      </MainContentWrapper>

      <MainContentWrapper sx={{ mt: 5 }}>
        <StyledTableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Product</TableCell>
                <TableCell>Purchase Date</TableCell>
                <TableCell>Starting Price</TableCell>
                <TableCell>Quantity</TableCell>
                <TableCell>Subtotal</TableCell>
                <TableCell>Discount</TableCell>
                <TableCell>Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>{getProductDescription(transaction)}</TableCell>
                <TableCell>
                  {transaction?.createdAt
                    ? new Date(transaction.createdAt).toLocaleDateString()
                    : 'N/A'}
                </TableCell>
                <TableCell>
                  {formatCurrency(transaction.prices?.startingPrice ?? 0)}
                </TableCell>
                <TableCell>{transaction.quantity}</TableCell>
                <TableCell>
                  {formatCurrency(transaction.prices?.subtotal ?? 0)}
                </TableCell>
                <TableCell>
                  {formatCurrency(
                    transaction.prices?.discountDetails?.amount ?? 0,
                  )}
                </TableCell>
                <TableCell>
                  {formatCurrency(transaction?.amount || 0)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </StyledTableContainer>
        <Grid container spacing={2} sx={{ mt: 2 }} direction="row">
          <Grid item xs={12} lg={6}>
            <List>
              <ListItem
                secondaryAction={
                  <IconButton
                    disabled={updateLoading}
                    onClick={handleEditNoteClick}
                  >
                    {editNote ? <SendIcon /> : <EditIcon />}
                  </IconButton>
                }
              >
                <ListItemAvatar>
                  <Avatar>
                    <NotesIcon />
                  </Avatar>
                </ListItemAvatar>
                {editNote ? (
                  <TextField
                    name="note"
                    label="Notes"
                    fullWidth
                    disabled={updateLoading}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />
                ) : (
                  <ListItemText
                    primary={transaction.note ? 'Notes' : 'No Notes'}
                    secondary={transaction.note}
                  />
                )}
              </ListItem>
            </List>
          </Grid>
          <Grid item xs={12} lg={6}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              {/* <StyledButton
                variant="contained"
                startIcon={<FileDownloadIcon />}
                onClick={handleDownloadInvoice}
              >
                Download Invoice
              </StyledButton> */}
              <List>
                {transaction?.serials.map((serial, index) => (
                  <ListItem key={`id-${index}`}>
                    <ListItemText primary={`Serial ${index + 1}: ${serial}`} />
                  </ListItem>
                ))}
              </List>
            </Box>
          </Grid>
        </Grid>
      </MainContentWrapper>
    </Container>
  );
};

export default PaymentDetailsPage;
